import { normalizeToAllowedValue } from './soteriaPickListNormalize';

const SOTERIA_REAL_ESTATE_SECTOR_VALUES = [
  'Hospitality',
  'Residential',
  'Commercial',
  'Entrepreneurship',
  'Construction',
  'Mostly Hospitality',
  'Mostly Residential',
  'Mostly Commercial',
  'Mostly Entrepreneurship',
  'Mostly Construction',
  'Mixed',
] as const;

/**
 * Maps property-type / sector-like text to a canonical Soteria real-estate sector.
 * Mirrors backend soteriaRealEstateSector.ts.
 */
export function inferRealEstateSectorFromText(value: string): string | null {
  const text = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!text) return null;
  if (/\b(hotel|motel|hospitality|resort|inn|lodging)\b/.test(text)) return 'Hospitality';
  if (
    /\b(multifamily|multi\s*family|multifam\w*|multyfam\w*|residential|apartment|condo|condominium|1\s*4\s*family|single\s*family|sfr|housing|townhome|townhouse)\b/.test(
      text
    )
  ) {
    return 'Residential';
  }
  if (
    /\b(commercial|office|retail|industrial|warehouse|self\s*storage|storage|medical\s*office|mixed\s*use)\b/.test(
      text
    )
  ) {
    return 'Commercial';
  }
  if (/\b(construction|development|ground\s*up|renovation|rehab)\b/.test(text)) {
    return 'Construction';
  }
  if (/\b(entrepreneurship|business|operating\s*company)\b/.test(text)) {
    return 'Entrepreneurship';
  }
  return null;
}

export function isRealEstateSectorAllowedList(allowedValues: readonly string[]): boolean {
  if (allowedValues.length === 0) return false;
  const canonical = SOTERIA_REAL_ESTATE_SECTOR_VALUES;
  return (
    allowedValues.length === canonical.length &&
    allowedValues.every((value) => canonical.includes(value as (typeof canonical)[number]))
  );
}

export function normalizeToRealEstateSectorAllowedValue(
  raw: string,
  allowedValues: readonly string[]
): string | null {
  const direct = normalizeToAllowedValue(raw, allowedValues);
  if (direct) return direct;

  const trimmed = raw.trim();
  if (!trimmed) return null;

  const mostlyMatch = trimmed.match(/^mostly\s+(.+)$/i);
  if (mostlyMatch?.[1]) {
    const inner = normalizeToRealEstateSectorAllowedValue(mostlyMatch[1], allowedValues);
    if (inner) {
      const mostlyValue = `Mostly ${inner}`;
      if (allowedValues.includes(mostlyValue)) return mostlyValue;
    }
  }

  const inferred = inferRealEstateSectorFromText(trimmed);
  if (inferred && allowedValues.includes(inferred)) return inferred;

  return null;
}
